import { useContext } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { deleteSchedule } from "@/api/schedule";
import { Button } from "@/components/common/Button";
import { Icon } from "@/components/common/Icon";
import { ModalAlert } from "@/components/common/modals";
import { useDepartmentNavigate } from "@/hooks/common/use-department-navigate";
import { useModal } from "@/hooks/common/use-modal";
import { showToast } from "@/providers";
import { RoutePathEnum } from "@/routes/paths";
import { formatDateInSaoPauloTimeZone } from "@/utils/date";

import { ScheduleActionsContext } from "../Provider";

export const ScheduleActionsDelete = () => {
  const modal = useModal();
  const queryClient = useQueryClient();
  const navigate = useDepartmentNavigate();
  const { schedule } = useContext(ScheduleActionsContext);
  const mutation = useMutation({
    mutationFn: deleteSchedule,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["schedule", "agenda"], refetchType: "active" }),
        queryClient.invalidateQueries({ queryKey: ["schedule", "stage-counts"], refetchType: "active" }),
      ]);
      showToast({ type: "success", title: "Agendamento excluído com sucesso." });
      navigate(RoutePathEnum.SCHEDULE);
    },
    onError: () =>
      showToast({
        type: "error",
        title: "Não foi possível excluir o agendamento.",
        content: "Tente novamente em alguns instantes.",
      }),
  });
  const canDelete =
    schedule.status === "AWAITING" &&
    schedule.currentStage === "SCHEDULED" &&
    !schedule.isPresent &&
    Boolean(schedule.scheduleDate) &&
    formatDateInSaoPauloTimeZone(new Date(schedule.scheduleDate!)) >= formatDateInSaoPauloTimeZone();

  if (!canDelete) {
    return null;
  }

  return (
    <>
      <Button variant="tertiary" full onClick={modal.openModal}>
        <Icon name="Trash" className="text-error" />
        <span className="font-regular text-error">Excluir agendamento</span>
      </Button>
      <ModalAlert
        isOpen={modal.isModalOpen}
        onClose={mutation.isPending ? undefined : modal.closeModal}
        alertIcon={{ icon: "Delete", variant: "error" }}
        title="Deseja excluir o agendamento?"
        description="Esta ação é irreversível. Se precisar, você poderá criar um novo agendamento através da agenda."
        actions={[
          ["Voltar", modal.closeModal],
          {
            children: "Sim, excluir agendamento",
            variant: "destructive",
            leading: "Delete",
            isLoading: mutation.isPending,
            onClick: () => mutation.mutate(schedule.id),
          },
        ]}
      />
    </>
  );
};
